import ExcelJS from "exceljs";

const HEADER_FILL = "FFDCE6F1"; // light blue header (matches the camp summary sheet)
const HEADER_FONT = "FF1F3864"; // dark navy header text
const BORDER_COLOR = "FF000000"; // thin black border on every cell
const TEXT_COLOR = "FF333333";
const FONT_NAME = "Calibri";

const SCHOOL_COL_WIDTH = 38;
const DAY_COL_WIDTH = 14;
const TIME_COL_WIDTH = 20;
const DURATION_COL_WIDTH = 16;
const COUNT_COL_WIDTH = 12;

const allBorders = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

function styleHeaderCell(cell) {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  cell.font = { name: FONT_NAME, bold: true, size: 11, color: { argb: HEADER_FONT } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.border = allBorders;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
const naturalCompare = (a, b) => collator.compare(String(a || ""), String(b || ""));

// School programs have no weeks and no AM/PM camp types, so the summary pivots
// by school instead: school -> stream -> count of distinct students. Pending
// rows arrive with student_id = null, so distinct students are keyed by
// student_id when present and by student_name otherwise (both still count).
const studentKey = (r) =>
  r.student_id != null && r.student_id !== ""
    ? `id:${r.student_id}`
    : `name:${r.student_name || ""}`;

/**
 * Pivots the School Program Registered Student List rows into:
 * school -> stream -> distinct student count, with per-school subtotals and an
 * overall grand total. Each stream carries the program day/time/duration where
 * available.
 */
export const buildSchoolProgramSummary = (reports) => {
  // Streams are keyed by course code so distinct courses never merge, even when
  // their abbreviations collide. Distinct students are tracked in a Set.
  const schoolMap = new Map();
  (reports || []).forEach((r) => {
    const school = r.school_name || "(no school)";
    if (!schoolMap.has(school)) {
      schoolMap.set(school, {
        school,
        schoolId: r.school_information_id || "",
        streamMap: new Map(),
      });
    }
    const streamMap = schoolMap.get(school).streamMap;
    const abbr = r.course_abbr || "(no stream)";
    const key = r.course_code || abbr;
    if (!streamMap.has(key)) {
      streamMap.set(key, {
        abbr,
        code: r.course_code || "",
        program: r.program_type || "",
        day: r.day || "",
        time: r.program_time || "",
        duration: r.program_duration || "",
        students: new Set(),
      });
    }
    const stream = streamMap.get(key);
    // Carry day/time/duration through from the first row that has them.
    if (!stream.day) stream.day = r.day || "";
    if (!stream.time) stream.time = r.program_time || "";
    if (!stream.duration) stream.duration = r.program_duration || "";
    stream.students.add(studentKey(r));
  });

  // Streams ordered by course code then abbreviation — mirrors the School
  // Program Attendance row ordering.
  const compareStreams = (a, b) =>
    naturalCompare(a.code, b.code) || naturalCompare(a.abbr, b.abbr);

  const schools = [...schoolMap.values()]
    .sort((a, b) => naturalCompare(a.school, b.school))
    .map(({ school, schoolId, streamMap }) => {
      const streams = [...streamMap.values()].sort(compareStreams).map((s) => ({
        abbr: s.abbr,
        code: s.code,
        program: s.program,
        day: s.day,
        time: s.time,
        duration: s.duration,
        count: s.students.size,
      }));
      const total = streams.reduce((sum, s) => sum + s.count, 0);
      return { school, schoolId, streams, total };
    });

  const grand = {
    total: schools.reduce((sum, s) => sum + s.total, 0),
  };

  return { schools, grand };
};

export const generateSchoolProgramSummaryWorkbook = async (reports) => {
  const { schools, grand } = buildSchoolProgramSummary(reports);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Summary", {
    views: [{ state: "frozen", ySplit: 1 }], // keep the header visible while scrolling
  });

  // Columns: School / Stream | Day | Time | Duration | Students
  const schoolCol = 1;
  const dayCol = 2;
  const timeCol = 3;
  const durationCol = 4;
  const countCol = 5;

  worksheet.getColumn(schoolCol).width = SCHOOL_COL_WIDTH;
  worksheet.getColumn(dayCol).width = DAY_COL_WIDTH;
  worksheet.getColumn(timeCol).width = TIME_COL_WIDTH;
  worksheet.getColumn(durationCol).width = DURATION_COL_WIDTH;
  worksheet.getColumn(countCol).width = COUNT_COL_WIDTH;

  // ---- Header (row 1) ----
  worksheet.getCell(1, schoolCol).value = "School / Stream";
  worksheet.getCell(1, dayCol).value = "Day";
  worksheet.getCell(1, timeCol).value = "Time";
  worksheet.getCell(1, durationCol).value = "Duration";
  worksheet.getCell(1, countCol).value = "Students";
  for (let c = schoolCol; c <= countCol; c++) {
    styleHeaderCell(worksheet.getCell(1, c));
  }
  worksheet.getRow(1).height = 22;

  // ---- Data rows ----
  const writeStream = (wsRow, stream) => {
    const font = { name: FONT_NAME, size: 11, color: { argb: TEXT_COLOR } };
    [
      [dayCol, stream.day],
      [timeCol, stream.time],
      [durationCol, stream.duration],
      [countCol, stream.count],
    ].forEach(([col, value]) => {
      const cell = wsRow.getCell(col);
      cell.value = value === "" ? null : value;
      cell.font = font;
      cell.alignment = { vertical: "middle", horizontal: col === countCol ? "center" : "left" };
    });
    for (let c = schoolCol; c <= countCol; c++) {
      wsRow.getCell(c).border = allBorders;
    }
  };

  let excelRow = 2;
  schools.forEach((school) => {
    const schoolRow = worksheet.getRow(excelRow);
    schoolRow.height = 20;
    const schoolCell = schoolRow.getCell(schoolCol);
    schoolCell.value = school.school;
    schoolCell.font = { name: FONT_NAME, size: 11, bold: true, color: { argb: TEXT_COLOR } };
    schoolCell.alignment = { vertical: "middle", horizontal: "left" };
    const schoolTotal = schoolRow.getCell(countCol);
    schoolTotal.value = school.total;
    schoolTotal.font = { name: FONT_NAME, size: 11, bold: true, color: { argb: TEXT_COLOR } };
    schoolTotal.alignment = { vertical: "middle", horizontal: "center" };
    for (let c = schoolCol; c <= countCol; c++) {
      schoolRow.getCell(c).border = allBorders;
    }
    excelRow += 1;

    school.streams.forEach((stream) => {
      const streamRow = worksheet.getRow(excelRow);
      streamRow.height = 18;
      const streamCell = streamRow.getCell(schoolCol);
      streamCell.value = stream.abbr;
      streamCell.font = { name: FONT_NAME, size: 11, color: { argb: TEXT_COLOR } };
      streamCell.alignment = { vertical: "middle", horizontal: "left", indent: 2 };
      writeStream(streamRow, stream);
      excelRow += 1;
    });
  });

  // Grand total row, banded like the header.
  const totalRow = worksheet.getRow(excelRow);
  totalRow.height = 20;
  totalRow.getCell(schoolCol).value = "Grand Total";
  totalRow.getCell(countCol).value = grand.total;
  for (let c = schoolCol; c <= countCol; c++) {
    styleHeaderCell(totalRow.getCell(c)); // restyles, keeps values
  }
  totalRow.getCell(schoolCol).alignment = { vertical: "middle", horizontal: "left" };

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadSchoolProgramSummarySheet = async (reports, branchName) => {
  const blob = await generateSchoolProgramSummaryWorkbook(reports);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${branchName} - School Program Summary.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
